import * as math from 'mathjs';
import * as geo from 'geodesy/latlon-ellipsoidal-vincenty';
interface VectorPoint {
  x: number;
  y: number;
}



export class Vector {
  from: math.Matrix;
  to: math.Matrix;
  vector: math.Matrix;
  direction: math.Matrix;
  _length: number = 0;
  get length() {
    if (this._length === 0) {
      this._length = math.norm(this.vector) as number;
    }
    return this._length;
  }

  constructor(from: math.Matrix, to: math.Matrix) {
    this.from = from;
    this.to = to;
    this.vector = math.subtract(this.to, this.from) as math.Matrix;
    this.direction = math.divide(this.vector, math.norm(this.vector)) as math.Matrix;
  }
  bearingTo(): number {
    const angleRad = Math.atan2(this.to.get([1]) - this.from.get([1]), this.to.get([0]) - this.from.get([0])) as number;
    let angleDeg = math.unit(angleRad, 'rad').toNumber('deg')
    angleDeg = angleDeg < 0 ? angleDeg + 360 : angleDeg;
    return 90 - angleDeg;
  }
  bearingFrom(): number {
    const angleRad = Math.atan2(this.from.get([1]) - this.to.get([1]), this.from.get([0]) - this.to.get([0]));
    let angleDeg = math.unit(angleRad, 'rad').toNumber('deg');
    angleDeg = angleDeg < 0 ? angleDeg + 360 : angleDeg;
    return 90 - angleDeg;
  }
  rotateTo(angleDeg: number): void {
    const angleRad = math.unit(angleDeg, 'deg').toNumber('rad');
    const rotatedX = this.from.get([0]) + this.length * Math.cos(angleRad);
    const rotatedY = this.from.get([1]) + this.length * Math.sin(angleRad);
    this.to = math.matrix([rotatedX, rotatedY]);
  }
  rotateFrom(angleDeg: number): void {
    const angleRad = math.unit(angleDeg, 'deg').toNumber('rad');
    const rotatedX = this.to.get([0]) + this.length * Math.cos(angleRad);
    const rotatedY = this.to.get([1]) + this.length * Math.sin(angleRad);
    this.from = math.matrix([rotatedX, rotatedY]);
  }
  rotateToRelative(angleDeg: number, angleType: 'bearing' | 'math' = 'bearing'): void {
    const originalBearing = this.bearingTo();
    let adjustedAngleDeg = originalBearing + angleDeg;
    if (angleType === 'bearing') {
      this.rotateTo(90);
      adjustedAngleDeg = (90 - originalBearing) - angleDeg;
    }
    this.rotateTo(adjustedAngleDeg);

  }

  rotateFromRelative(angleDeg: number, angleType: 'bearing' | 'math' = 'bearing'): void {
    const originalBearing = this.bearingFrom();
    let adjustedAngleDeg = originalBearing + angleDeg;
    if (angleType === 'bearing') {
      this.rotateTo(90);
      adjustedAngleDeg = (90 - originalBearing) - angleDeg;
    }
    this.rotateTo(adjustedAngleDeg);
  }
}



export function GetNearestPointOnInfiniteLine(vector: Vector, externalPoint: VectorPoint): VectorPoint {
  // Convert external point to mathjs matrix
  const externalMatrix = math.matrix([externalPoint.x, externalPoint.y]);

  // Calculate the vector from "From" point to the external point
  const vectorToExternal = math.subtract(externalMatrix, vector.from) as math.Matrix;

  // Calculate the distance along the line
  const distance = math.dot(vectorToExternal, vector.direction) as number;

  // Calculate the nearest point
  const nearestMatrix = math.add(vector.from, math.multiply(distance, vector.direction)) as math.Matrix;

  // Convert the result back to a Point interface
  const nearestPoint: VectorPoint = {
    x: nearestMatrix.get([0]),
    y: nearestMatrix.get([1]),
  };

  return nearestPoint;
}


export function GetNearestPointOnSegment(from: math.Matrix, to: math.Matrix, externalPoint: VectorPoint): VectorPoint {
  // Create Vector instance
  const vector = new Vector(from, to);

  // Convert external point to mathjs matrix
  const externalMatrix = math.matrix([externalPoint.x, externalPoint.y]);

  // Calculate the vector from "From" point to the external point
  const vectorToExternal = math.subtract(externalMatrix, vector.from) as math.Matrix;

  // Calculate the distance along the segment
  const distance = math.dot(vectorToExternal, vector.direction) as number;

  // Clamp the distance to ensure it's within the segment's range
  const clampedDistance = Math.max(0, Math.min(math.norm(vector.vector) as number, distance));

  // Calculate the nearest point
  const nearestMatrix = math.add(vector.from, math.multiply(clampedDistance, vector.direction)) as math.Matrix;

  // Convert the result back to a Point interface
  const nearestPoint: VectorPoint = {
    x: nearestMatrix.get([0]),
    y: nearestMatrix.get([1]),
  };

  return nearestPoint;
}
